// src/components/MediaPickerDialog.jsx
import { useState, useEffect, useRef } from "react";
import {
  X,
  Search,
  Link2,
  Image as ImageIcon,
  Film,
  Check,
  Upload,
  Loader2,
} from "lucide-react";
import { showError } from "../lib/toast";
import {
  server_post_data,
  get_media_library,
  APL_LINK,
} from "../serviceconnection/ServiceConnection";

// ── Dialog component ──────────────────────────────────────────────────────────
function MediaPickerDialog({ open, onClose, onSelect, accept = "all" }) {
  const [tab, setTab] = useState("library");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState("");

  // ── Dynamic state ─────────────────────────────────────────────────────────
  const [mediaList, setMediaList] = useState([]);
  const [imageLinkPrefix, setImageLinkPrefix] = useState("");
  const [loading, setLoading] = useState(false);

  const overlayRef = useRef(null);

  // ── Fetch media from backend ───────────────────────────────────────────────
  const fetchMedia = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_media_library, fd);
      setMediaList(res.data?.data ?? []);
      setImageLinkPrefix(APL_LINK + (res.data?.path_image ?? ""));
    } catch {
      showError("Failed to load media library");
    } finally {
      setLoading(false);
    }
  };

  // ── Reset + fetch when dialog opens ──────────────────────────────────────
  useEffect(() => {
    if (open) {
      setSelected(null);
      setUrlInput("");
      setUrlError("");
      setSearch("");
      setTab("library");
      fetchMedia();
    }
  }, [open]);

  // ── Close on Escape ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  // ── Map backend item → dialog-friendly shape ──────────────────────────────
  const toMedia = (f) => ({
    id: f.primary_id,
    type: f.file_type, // "image" | "video" | "document"
    url: imageLinkPrefix + f.generated_name,
    thumb: imageLinkPrefix + f.generated_name, // same URL for thumb
    name: f.original_name,
    size: f.file_size,
  });

  // ── Filter list ───────────────────────────────────────────────────────────
  const visible = mediaList
    .filter((f) => {
      const matchAccept = accept === "all" || f.file_type === accept;
      const matchFilter = filter === "all" || f.file_type === filter;
      const matchSearch = f.original_name
        ?.toLowerCase()
        .includes(search.toLowerCase());
      return matchAccept && matchFilter && matchSearch;
    })
    .map(toMedia);

  // ── Confirm ────────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (tab === "url") {
      if (!urlInput.trim()) {
        setUrlError("Please enter a URL");
        return;
      }
      onSelect({ url: urlInput.trim(), type: "url", name: urlInput.trim() });
    } else {
      if (!selected) return;
      onSelect(selected);
    }
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="relative flex flex-col rounded-2xl overflow-hidden shadow-2xl"
        style={{
          width: "min(1200px, 96vw)",
          height: "min(860px, 93vh)",
          background: "hsl(var(--background))",
          border: "1px solid hsl(var(--border))",
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: "1px solid hsl(var(--border))" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "hsl(var(--accent-muted))" }}
            >
              <ImageIcon
                className="w-4 h-4"
                style={{ color: "hsl(var(--accent))" }}
              />
            </div>
            <div>
              <h2
                className="text-sm font-semibold"
                style={{ color: "hsl(var(--foreground))" }}
              >
                Media Library
              </h2>
              <p
                className="text-xs"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                Select from library or enter a URL
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-muted"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div
          className="flex gap-1 px-5 pt-3 pb-0 shrink-0"
          style={{ borderBottom: "1px solid hsl(var(--border))" }}
        >
          {[
            {
              key: "library",
              icon: <ImageIcon className="w-3.5 h-3.5" />,
              label: "Media Library",
            },
            {
              key: "url",
              icon: <Link2 className="w-3.5 h-3.5" />,
              label: "Enter URL",
            },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors"
              style={{
                color:
                  tab === t.key
                    ? "hsl(var(--accent))"
                    : "hsl(var(--muted-foreground))",
                background:
                  tab === t.key ? "hsl(var(--accent-muted))" : "transparent",
                borderBottom:
                  tab === t.key
                    ? `2px solid hsl(var(--accent))`
                    : "2px solid transparent",
                marginBottom: "-1px",
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* ─ Library Tab ─ */}
          {tab === "library" && (
            <>
              {/* Toolbar */}
              <div
                className="flex items-center gap-3 px-5 py-3 shrink-0"
                style={{ borderBottom: "1px solid hsl(var(--border))" }}
              >
                <div className="relative flex-1">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                    style={{ color: "hsl(var(--muted-foreground))" }}
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search files…"
                    className="w-full text-xs rounded-lg pl-8 pr-3 py-2 outline-none focus:ring-2"
                    style={{
                      background: "hsl(var(--muted))",
                      border: "1px solid hsl(var(--border))",
                      color: "hsl(var(--foreground))",
                    }}
                  />
                </div>
                {/* Filter pills */}
                <div className="flex gap-1">
                  {["all", "image", "video"].map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors"
                      style={{
                        background:
                          filter === f
                            ? "hsl(var(--accent-muted))"
                            : "hsl(var(--muted))",
                        color:
                          filter === f
                            ? "hsl(var(--accent))"
                            : "hsl(var(--muted-foreground))",
                        border: `1px solid ${filter === f ? "hsl(var(--accent))" : "hsl(var(--border))"}`,
                      }}
                    >
                      {f === "image" && <ImageIcon className="w-3 h-3" />}
                      {f === "video" && <Film className="w-3 h-3" />}
                      {f}
                    </button>
                  ))}
                </div>
                <span
                  className="text-xs shrink-0"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {loading
                    ? "Loading…"
                    : `${visible.length} file${visible.length !== 1 ? "s" : ""}`}
                </span>
              </div>

              {/* Grid */}
              <div className="flex-1 overflow-y-auto p-5">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2
                      className="w-8 h-8 animate-spin"
                      style={{ color: "hsl(var(--accent))" }}
                    />
                  </div>
                ) : visible.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center gap-2">
                    <Search
                      className="w-8 h-8"
                      style={{ color: "hsl(var(--muted-foreground))" }}
                    />
                    <p
                      className="text-sm"
                      style={{ color: "hsl(var(--muted-foreground))" }}
                    >
                      No files found
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {visible.map((m) => {
                      const isSel = selected?.id === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setSelected(m)}
                          className="group relative rounded-xl overflow-hidden text-left transition-all"
                          style={{
                            border: `2px solid ${isSel ? "hsl(var(--accent))" : "hsl(var(--border))"}`,
                            background: "hsl(var(--muted))",
                            boxShadow: isSel
                              ? "0 0 0 3px hsl(var(--accent) / 0.2)"
                              : "none",
                          }}
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                            {m.type === "image" ? (
                              <img
                                src={m.thumb}
                                alt={m.name}
                                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film
                                  className="w-8 h-8"
                                  style={{ color: "hsl(280 55% 55%)" }}
                                />
                              </div>
                            )}
                            {m.type === "video" && (
                              <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{ background: "rgba(0,0,0,0.35)" }}
                              >
                                <Film className="w-6 h-6 text-white" />
                              </div>
                            )}
                            {isSel && (
                              <div
                                className="absolute inset-0 flex items-center justify-center"
                                style={{
                                  background: "hsl(var(--accent) / 0.3)",
                                }}
                              >
                                <div
                                  className="w-7 h-7 rounded-full flex items-center justify-center"
                                  style={{ background: "hsl(var(--accent))" }}
                                >
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Meta */}
                          <div className="px-2 py-1.5">
                            <p
                              className="text-xs font-medium truncate"
                              style={{ color: "hsl(var(--foreground))" }}
                            >
                              {m.name}
                            </p>
                            <p
                              className="text-xs"
                              style={{ color: "hsl(var(--muted-foreground))" }}
                            >
                              {m.size}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ─ URL Tab ─ */}
          {tab === "url" && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: "hsl(var(--accent-muted))" }}
              >
                <Link2
                  className="w-6 h-6"
                  style={{ color: "hsl(var(--accent))" }}
                />
              </div>
              <div className="w-full max-w-md space-y-2">
                <label
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  Image or Video URL
                </label>
                <input
                  value={urlInput}
                  onChange={(e) => {
                    setUrlInput(e.target.value);
                    setUrlError("");
                  }}
                  placeholder="https://example.com/image.jpg"
                  className="w-full text-sm rounded-xl px-4 py-3 outline-none focus:ring-2"
                  style={{
                    background: "hsl(var(--muted))",
                    border: `1px solid ${urlError ? "hsl(var(--destructive))" : "hsl(var(--border))"}`,
                    color: "hsl(var(--foreground))",
                  }}
                />
                {urlError && (
                  <p
                    className="text-xs"
                    style={{ color: "hsl(var(--destructive))" }}
                  >
                    {urlError}
                  </p>
                )}
                <p
                  className="text-xs"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  Paste a direct link to an image (.jpg, .png, .webp) or video
                  (.mp4, .webm).
                </p>
              </div>
              {/* Live preview */}
              {urlInput.trim() && (
                <div
                  className="w-full max-w-md rounded-xl overflow-hidden"
                  style={{ border: "1px solid hsl(var(--border))" }}
                >
                  <img
                    src={urlInput.trim()}
                    alt="preview"
                    className="w-full max-h-40 object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                    onLoad={(e) => {
                      e.target.style.display = "block";
                    }}
                    style={{ display: "none" }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderTop: "1px solid hsl(var(--border))" }}
        >
          <div
            className="text-xs"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            {tab === "library" && selected ? (
              <span className="flex items-center gap-1.5">
                <Check
                  className="w-3.5 h-3.5"
                  style={{ color: "hsl(var(--accent))" }}
                />
                <strong style={{ color: "hsl(var(--foreground))" }}>
                  {selected.name}
                </strong>{" "}
                selected
              </span>
            ) : tab === "library" ? (
              "No file selected"
            ) : urlInput ? (
              "URL entered"
            ) : (
              "No URL entered"
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-semibold transition-colors hover:bg-muted"
              style={{
                color: "hsl(var(--muted-foreground))",
                border: "1px solid hsl(var(--border))",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={tab === "library" ? !selected : !urlInput.trim()}
              className="px-5 py-2 rounded-lg text-xs font-semibold text-white transition-opacity"
              style={{
                background: "hsl(var(--accent))",
                opacity: (tab === "library" ? !selected : !urlInput.trim())
                  ? 0.45
                  : 1,
              }}
            >
              Insert Media
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MediaImageField ───────────────────────────────────────────────────────────
// Usage:
//   <MediaImageField
//     label="Upload snapshot image"
//     hint="800×600px"
//     alt={snapImageAlt}
//     onAltChange={setSnapImageAlt}
//     value={snapImage}
//     onChange={setSnapImage}
//   />
// ─────────────────────────────────────────────────────────────────────────────
export function MediaImageField({
  label,
  hint,
  alt,
  onAltChange,
  value,
  onChange,
  accept = "all",
}) {
  const [open, setOpen] = useState(false);

  const handleSelect = (media) => {
    onChange?.(media.url);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.("");
    onAltChange?.("");
  };

  const isVideo = (url) =>
    url &&
    (url.includes(".mp4") ||
      url.includes(".webm") ||
      url.includes(".ogg") ||
      url.includes(".mov"));

  return (
    <>
      <div className="space-y-2">
        {value ? (
          <div
            className="relative rounded-xl overflow-hidden group cursor-pointer"
            style={{ border: "1px solid hsl(var(--border))" }}
            onClick={() => setOpen(true)}
          >
            {isVideo(value) ? (
              <video
                src={value}
                className="w-full max-h-40 object-cover"
                muted
                playsInline
                loop
                autoPlay
              />
            ) : (
              <img
                src={value}
                alt={alt || label}
                className="w-full max-h-40 object-cover"
              />
            )}
            <div
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(0,0,0,0.45)" }}
            >
              <span
                className="text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background: "hsl(var(--accent))" }}
              >
                Change
              </span>
            </div>
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(0,0,0,0.6)" }}
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full flex flex-col items-center justify-center gap-2 rounded-xl py-6 transition-colors hover:opacity-80"
            style={{
              border: "2px dashed hsl(var(--border))",
              background: "hsl(var(--muted))",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "hsl(var(--accent-muted))" }}
            >
              <Upload
                className="w-4 h-4"
                style={{ color: "hsl(var(--accent))" }}
              />
            </div>
            <div className="text-center">
              <p
                className="text-xs font-semibold"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {label || "Select media"}
              </p>
              {hint && <p className="text-xs mt-0.5">{hint}</p>}
            </div>
          </button>
        )}

        {onAltChange !== undefined && (
          <input
            value={alt ?? ""}
            onChange={(e) => onAltChange(e.target.value)}
            placeholder="Alt text (for accessibility & SEO)"
            className="w-full text-xs rounded-lg px-3 py-2 outline-none focus:ring-2"
            style={{
              background: "hsl(var(--muted))",
              border: "1px solid hsl(var(--border))",
              color: "hsl(var(--foreground))",
            }}
          />
        )}
      </div>

      <MediaPickerDialog
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
        accept={accept}
      />
    </>
  );
}

export default MediaPickerDialog;
